import { useMemo } from "react";
import SearchableSelect from "components/common/select/SearchableSelect";
import { ProductTypeDto } from "apiClients/useProductTypeApiClient";
import { useProductTypesQuery } from "domains/productType/hooks/useProductTypesQuery";
import { compareProductTypes } from "domains/productType/productTypeTree";

interface ProductTypeSelectProps {
  editorProps: any;
  placeholder?: string;
}

const buildPathLabel = (
  productType: ProductTypeDto,
  byId: Map<number, ProductTypeDto>
): string => {
  const parts: string[] = [];
  let current: ProductTypeDto | undefined = productType;

  while (current) {
    parts.unshift(current.name || `Typ #${current.id}`);
    current = current.parentProductTypeId
      ? byId.get(current.parentProductTypeId)
      : undefined;
  }

  return parts.join(" › ");
};

const ProductTypeSelectGrid = ({ editorProps, placeholder }: ProductTypeSelectProps) => {
  const { data: productTypes = [] } = useProductTypesQuery();

  const options = useMemo(() => {
    const byId = new Map(productTypes.map((pt) => [pt.id, pt]));
    const byParent = new Map<number | null, ProductTypeDto[]>();
    productTypes.forEach((pt) => {
      const parentKey = pt.parentProductTypeId ?? null;
      const list = byParent.get(parentKey) ?? [];
      list.push(pt);
      byParent.set(parentKey, list);
    });
    byParent.forEach((list, key) => byParent.set(key, list.slice().sort(compareProductTypes)));

    const ordered: ProductTypeDto[] = [];
    const visit = (parentId: number | null) => {
      (byParent.get(parentId) ?? []).forEach((pt) => {
        ordered.push(pt);
        visit(pt.id);
      });
    };
    visit(null);

    return ordered.map((productType) => ({
      value: productType.id,
      label: buildPathLabel(productType, byId),
    }));
  }, [productTypes]);

  const onChange = async (value: string | number | null) => {
    const nextValue =
      value === undefined || value === null || value === ""
        ? null
        : Number(value);
    await editorProps.onChange(nextValue);
    editorProps.onComplete();
  };

  return (
    <SearchableSelect
      defaultOpen={true}
      options={options}
      onChange={onChange}
      placeholder={placeholder ?? ""}
      isTableCell={true}
    />
  );
};

export default ProductTypeSelectGrid;
